import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { TourProvider } from '@reactour/tour';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ToastContainer } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import ThemeContext from '../contexts/themeContext';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
import AsideRoutes from '../layout/Aside/AsideRoutes';
import { ToastCloseButton } from '../components/bootstrap/Toasts';
import { useAccessControl } from '../contexts/AccessControlContext';

// Public paths that render immediately without waiting for the auth check
const PUBLIC_PATHS_NO_AUTH = ['/', '/auth-pages/login', '/auth-pages/sign-up', '/auth-pages/404', '/privacy-policy', '/reporter-privacy-policy', '/delete-account', '/reporter-delete-account', '/contact-us'];

/** Returns true for any path that should bypass the auth-initialization loading screen. */
const isPublicPathname = (pathname: string): boolean =>
	PUBLIC_PATHS_NO_AUTH.includes(pathname) || pathname.startsWith('/verify/');

const App = () => {
	getOS();

	dayjs.extend(localizedFormat);
	dayjs.extend(relativeTime);

	/**
	 * Auth initialization guard.
	 * Blocks ALL app shell rendering (sidebar, header, footer) until the global
	 * auth check against the server completes. This prevents unauthenticated users
	 * from ever seeing the dashboard layout before being redirected to login.
	 *
	 * Exception: public paths (landing page, login, sign-up) render immediately
	 * without waiting, so they are visible instantly without any blank-screen delay.
	 */
	const { isInitializing } = useAccessControl();
	const { pathname } = useLocation();
	const isPublicPath = isPublicPathname(pathname);

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
			document.documentElement.setAttribute('data-bs-theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
			document.documentElement.removeAttribute('data-bs-theme');
		};
	}, [darkModeStatus]);

	/**
	 * Full Screen
	 */
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	// @ts-ignore
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	return (
		<ThemeProvider theme={theme}>
			<TourProvider steps={steps} styles={styles} showNavigation={false} showBadge={false}>
				{/* Block the app shell until the one-time server auth check finishes.
				    Exception: public paths (landing, login, sign-up) render immediately
				    so they're never gated behind the auth API call. */}
				{isInitializing && !isPublicPath ? (
					<div
						style={{
							position: 'fixed',
							inset: 0,
							backgroundColor: 'var(--bs-body-bg, #ffffff)',
						}}
						aria-hidden='true'
					/>
				) : (
					<div
						ref={ref}
						className='app'
						style={{
							backgroundColor: fullScreenStatus ? 'var(--bs-body-bg)' : undefined,
							zIndex: fullScreenStatus ? 1 : undefined,
							overflow: fullScreenStatus ? 'scroll' : undefined,
						}}>
						<AsideRoutes />
						<Wrapper />
					</div>
				)}
				<Portal id='portal-notification'>
					<ReactNotifications />
				</Portal>
				<ToastContainer closeButton={ToastCloseButton} toastClassName='toast show' />
			</TourProvider>
		</ThemeProvider>
	);
};

export default App;
